import { useWhoAmIQuery } from 'api/authentication'
import { dispatch } from 'api/redux'
import { showSnackbar } from 'components/snackbar'
import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { isBrimley } from './dcContext'

const validDPIVersions = ['200', '300']
const UNIT_HANDLING_STATION_REQUEST_PARAM = 'unitHandlingStation'
const DPI_VERSION_REQUEST_PARAM = 'dpiVersion'

export const useAuthentication = () => {
  const { t } = useTranslation()
  const { data: userData, isLoading, refetch: refetchWhoAmI } = useWhoAmIQuery()

  const params = new URLSearchParams(window.location.search)
  const [dpi, setDpi] = useState(params.get(DPI_VERSION_REQUEST_PARAM) ?? '300')
  const [station, setStation] = useState<string>(params.get(UNIT_HANDLING_STATION_REQUEST_PARAM) ?? '')

  useEffect(() => {
    if (params.has(DPI_VERSION_REQUEST_PARAM)) {
      setDpi(params.get(DPI_VERSION_REQUEST_PARAM)!)
    }

    if (params.has(UNIT_HANDLING_STATION_REQUEST_PARAM)) {
      setStation(params.get(UNIT_HANDLING_STATION_REQUEST_PARAM)!)
    }
  }, [params])

  useEffect(() => {
    if (isLoading) return

    if (!userData) {
      refetchWhoAmI()
      return
    }

    if (!userData.isAuthenticated && !isBrimley) {
      redirectToAuthenticationEndpoint()
    }
  }, [userData, isLoading, refetchWhoAmI])

  // Used to set cookies that allow the single-page React app to share data
  // (credentials and redirect URLs) with JavaScript returned by the backend
  // API, for autoredirect/auto form submit purposes.
  // The Max-Age is set to 5 minutes.
  // The Domain is set to the current hostname, ensuring the cookie will be
  // visible to JavaScript running under the backend API origin (which is on a
  // subdomain of the React App origin hostname).
  const setLocalTempCookie = (name: string, value: string) => {
    const cookie = `${name}=${value};max-age=300;domain=${window.location.hostname}`
    document.cookie = cookie
  }

  const hasValidParams = useCallback(() => {
    const errorMsg = station === ''
      ? t('snackbar.error_station_missing')
      : !validDPIVersions.includes(dpi)
          ? t('snackbar.error_dpi', { dpi })
          : ''
    if (errorMsg) {
      dispatch(showSnackbar(errorMsg, 'error'))
    }
    return !errorMsg
  }, [params])

  const redirectToAuthenticationEndpoint = useCallback(() => {
    if (!hasValidParams()) {
      return
    }

    // We clean this up to avoid double parameters and to get rid of login error parameters
    const encodedRedirectUrl = encodeURIComponent(`${window.location.origin}?unitHandlingStation=${station}&dpiVersion=${dpi}`)
    const encodedStation = encodeURIComponent(station)
    const encodedDpi = encodeURIComponent(dpi)

    setLocalTempCookie('redirectUrl', encodedRedirectUrl)
    window.location.assign(`${process.env.REACT_APP_API_URL!}/authenticate?redirectUrl=${encodedRedirectUrl}&unitHandlingStation=${encodedStation}&dpiVersion=${encodedDpi}`)
  }, [params])

  const storeCredentialsAndRedirectToAuthPoint = (username: string, password: string) => {
    setLocalTempCookie('username', username)
    setLocalTempCookie('password', password)
    redirectToAuthenticationEndpoint()
  }

  const isAuthenticated = useCallback(() => {
    if (isLoading) return false
    if (!hasValidParams()) return false
    return userData !== undefined &&
        userData.isAuthenticated
  }, [userData, params, isLoading, isBrimley])

  return {
    isAuthenticated: isAuthenticated(),
    isFetchingAuth: isLoading,
    storeCredentialsAndRedirectToAuthPoint,
    userData
  }
}
